import {
  AppLayout,
  Mtds,
  ScreenHeader,
  TopBar,
  WarningIcon,
  useGetPlansQuery,
  useGetWorkspaceGroupsQuery,
  useAuth
} from 'prove-common'
import { Link, Navigate, useSearchParams } from 'react-router'
import { AppLogo } from '../components/app-logo/AppLogo'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'
import { useTaskOverviewRows } from '../hooks/usePlanTasks'
import { getWorkspaceTaskSummary } from '../utils/tasksTableUtils'
import {
  collectRecursiveWorkspacePlanIds,
  filterPlansByWorkspace,
  filterUnassignedPlans,
  UNASSIGNED_WORKSPACE,
  unassignedWorkspaceDescription,
  unassignedWorkspaceName,
  useIsWorkspaceFeatureEnabled,
  WORKSPACE_QUERY_PARAM,
  toWorkspaceViewModels
} from '../workspaces/workspaces'

export default function WorkspacesScreen() {
  const [params] = useSearchParams()
  const { isAdmin, permissions = [] } = useAuth()
  const canBrowseUnassigned =
    isAdmin ||
    permissions.some(
      (permission) => permission.responsibility === 'PROGRAM_MANAGER'
    )
  const workspaceFeature = useIsWorkspaceFeatureEnabled()
  const { data: plans, isPending, isFetching, isError } = useGetPlansQuery()
  const {
    data: groups,
    isPending: isPendingWorkspaces,
    isFetching: isFetchingWorkspaces,
    isError: isErrorWorkspaces
  } = useGetWorkspaceGroupsQuery(workspaceFeature.enabled)
  // Reuses the ['tasks','proveplan-overview'] cache entry shared with the
  // tasks and plans screens.
  const { rows: taskRows, today } = useTaskOverviewRows()

  if (!workspaceFeature.isPending && !workspaceFeature.enabled) {
    return <Navigate to='/plans' replace />
  }

  const topLevelPlans = (plans?.plans ?? []).filter(
    (plan) => (plan?.parents?.length ?? 0) === 0
  )
  const requestedKind = params.get('kind')
  const group = requestedKind
    ? groups?.find((item) => item.kind.code === requestedKind)
    : groups?.[0]
  const kind = group?.kind.code
  const title = group?.kind.title ?? 'Arbeidsområder'
  const workspaces = toWorkspaceViewModels(group?.workspaces)
  const unassignedPlanCount = filterUnassignedPlans(
    topLevelPlans,
    workspaces,
    kind
  ).length

  return (
    <AppLayout
      useMobileBar={false}
      sidebar={<SideBarLayout />}
      isLoading={
        workspaceFeature.isPending ||
        isPending ||
        isFetching ||
        isPendingWorkspaces ||
        isFetchingWorkspaces
      }
      isError={isError || isErrorWorkspaces}
      topBar={
        <TopBar logo={<AppLogo />} title={title} showNotificationBell={true} />
      }
    >
      <ScreenHeader title={title} />
      {!isPendingWorkspaces && !group && (
        <Mtds.Card>
          Ingen arbeidsområder er tilgjengelige for dine tilganger.
        </Mtds.Card>
      )}
      <Mtds.Grid>
        {workspaces.map((workspace) => {
          const planCount = filterPlansByWorkspace(
            topLevelPlans,
            workspace.id
          ).length
          const taskSummary = getWorkspaceTaskSummary(
            taskRows,
            collectRecursiveWorkspacePlanIds(plans?.plans, workspace.id),
            today
          )

          return (
            <Mtds.Card
              key={workspace.id}
              as={Link}
              to={`/plans?${WORKSPACE_QUERY_PARAM}=${workspace.id}`}
              className='grid gap-4 lg:grid-cols-[minmax(0,1fr)_9rem] lg:items-start'
            >
              <Mtds.Prose>
                <Mtds.Heading as='h2'>{workspace.name}</Mtds.Heading>
                <p>{workspace.description}</p>
                <p>
                  {planCount} {planCount === 1 ? 'plan' : 'planer'}
                  {taskSummary.total > 0 && (
                    <>
                      {' · '}
                      {taskSummary.total}{' '}
                      {taskSummary.total === 1 ? 'oppgave' : 'oppgaver'}
                    </>
                  )}
                </p>
                {taskSummary.overdue > 0 && (
                  <p>
                    <Mtds.Tag data-color='danger'>
                      <WarningIcon aria-hidden />
                      {taskSummary.overdue}{' '}
                      {taskSummary.overdue === 1
                        ? 'forfalt frist'
                        : 'forfalte frister'}
                    </Mtds.Tag>
                  </p>
                )}
              </Mtds.Prose>
              <div
                aria-hidden='true'
                className='hidden justify-self-end lg:block'
              >
                <img
                  alt=''
                  className='h-36 w-36 object-contain'
                  src={workspace.illustration}
                />
              </div>
            </Mtds.Card>
          )
        })}
        {canBrowseUnassigned && group && unassignedPlanCount > 0 && (
          <Mtds.Card
            as={Link}
            to={`/plans?${WORKSPACE_QUERY_PARAM}=${UNASSIGNED_WORKSPACE}&kind=${kind}`}
            className='grid gap-4 lg:grid-cols-[minmax(0,1fr)_9rem] lg:items-start'
          >
            <Mtds.Prose>
              <Mtds.Heading as='h2'>
                {unassignedWorkspaceName(kind)}
              </Mtds.Heading>
              <p>{unassignedWorkspaceDescription(kind)}</p>
              <p>
                {unassignedPlanCount}{' '}
                {unassignedPlanCount === 1 ? 'plan' : 'planer'}
              </p>
            </Mtds.Prose>
          </Mtds.Card>
        )}
      </Mtds.Grid>
    </AppLayout>
  )
}
